import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import { ansichtSichtbar } from '@asas/domain/ansichten';
import type { KontoMitarbeiterOption, Permission, Principal, TenantRole } from '@asas/domain';
import { DOMAIN_VERSION } from '@asas/domain/version';

import { Projekte, darfStatusAendern } from './Projekte';
import { AdminPanel, meldungFuerKontoaktion } from './AdminPanel';
import { ArbeitspaketBibliothek } from './ArbeitspaketBibliothek';
import type { CurrentSession } from './api';
import { loadSession, logout } from './api';
import { entitaetsSchluessel, geteilterSerialisierer } from './daten/serialisierung';
import { klassifiziereFehler } from './daten/fehler';
import { darfFachgruppenPflegen } from './fachgruppen/fachgruppen';
import {
  darfKontenVerknuepfen,
  meldungFuerVerknuepfung,
  optionenNachVerknuepfung,
} from './konto-mitarbeiter';
import { FachgruppenBereich } from './fachgruppen/FachgruppenBereich';
import { darfLphGruppierungPflegen, darfLphVorschau } from './lph-gruppierung/lph-gruppierung';
import { LphGruppierungBereich } from './lph-gruppierung/LphGruppierungBereich';
import { darfApFormatPflegen, darfApImport } from './ap-import/ap-import';
import { ApImportBereich } from './ap-import/ApImportBereich';
import { MitarbeiterBereich } from './mitarbeiter/MitarbeiterBereich';
import { RestaufwandBereich } from './restaufwand/RestaufwandBereich';
import { darfWochenrasterLesen } from './wochenraster/wochenraster';
import { WochenrasterBereich } from './wochenraster/WochenrasterBereich';
import { AuslastungBereich } from './auslastung/AuslastungBereich';
import { darfNachtragen, darfPlanen } from './wochenraster/planung';
import {
  darfEntscheiden,
  darfOffeneAnfragenLesen,
  darfRessourcenAnfragen,
} from './anfragen/anfragen';
import { SessionPanel } from './SessionPanel';
import { trpc } from './trpc';
import { TrpcPanel } from './TrpcPanel';

export function App(): JSX.Element {
  const [session, setSession] = useState<CurrentSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [echo, setEcho] = useState<string | null>(null);
  const [trpcLoading, setTrpcLoading] = useState(true);
  const [trpcError, setTrpcError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(() =>
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('anmeldung')
      ? 'Die Anmeldung ist fehlgeschlagen. Bitte versuchen Sie es erneut.'
      : null,
  );

  // AC-107: Nutzerverwaltung. `roles` kommt aus `tenant.settings`
  // (ANY_AUTHENTICATED) statt aus einer eigenen Prozedur — die Liste existiert
  // dort bereits (CLAUDE.md, Entwurfsabschnitt 3).
  const [principals, setPrincipals] = useState<readonly Principal[]>([]);
  const [roles, setRoles] = useState<readonly TenantRole[]>([]);
  // Geladen wird erst nach einem Klick — siehe `AdminPanelProps.geoeffnet`.
  const [adminGeoeffnet, setAdminGeoeffnet] = useState(false);
  const [principalsLoading, setPrincipalsLoading] = useState(false);
  const [principalsError, setPrincipalsError] = useState<string | null>(null);
  const [busyIds, setBusyIds] = useState<ReadonlySet<string>>(new Set());
  const [rowErrors, setRowErrors] = useState<ReadonlyMap<string, string>>(new Map());
  // AC-107-1: interne Mitarbeiter zum Verknüpfen; `null` ohne `principals:manage`.
  const [mitarbeiterOptionen, setMitarbeiterOptionen] = useState<
    readonly KontoMitarbeiterOption[] | null
  >(null);
  const [optionenFehler, setOptionenFehler] = useState<string | null>(null);
  // AC-000: die eigenen Rechte. `null`, solange sie nicht feststehen — dann
  // wird keine Ansicht mit Rechtebedarf gezeigt (siehe `ANSICHTEN`).
  const [rechte, setRechte] = useState<ReadonlySet<Permission> | null>(null);
  const darfVerknuepfen = rechte !== null && darfKontenVerknuepfen(rechte);

  useEffect(() => {
    let mounted = true;

    void loadSession()
      .then((current) => {
        if (mounted) setSession(current);
      })
      .catch(() => {
        if (mounted)
          setError('Die Sitzung konnte nicht geprüft werden. Bitte versuchen Sie es erneut.');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  // AC-000: Ansichten nur zeigen, deren Ladeaufrufe die eigenen Rechte
  // erlauben. `session.permissions` ist ANY_AUTHENTICATED und erzeugt deshalb
  // nie ein 403 samt Audit-Zeile. Scheitert der Aufruf, bleibt alles mit
  // Rechtebedarf ausgeblendet; der Server lehnt ohnehin ab.
  useEffect(() => {
    let mounted = true;

    void trpc.session.permissions
      .query()
      .then((ergebnis) => {
        if (mounted) setRechte(new Set(ergebnis.permissions));
      })
      .catch(() => {
        if (mounted) setRechte(null);
      });

    return () => {
      mounted = false;
    };
  }, []);

  // Erster Aufruf ueber tRPC (AC-010). Der Endpunkt liegt hinter dem globalen
  // TenantGuard: ohne Sitzung antwortet Nest mit 401, und der Client scheitert
  // beim Auswerten. Die typisierte Behandlung dieses Falls gehoert zu AC-007.
  useEffect(() => {
    let mounted = true;

    void trpc.system.ping
      .query({ nachricht: 'hallo' })
      .then((result) => {
        if (mounted) setEcho(result.echo);
      })
      .catch(() => {
        if (mounted) setTrpcError('Der tRPC-Aufruf ist fehlgeschlagen.');
      })
      .finally(() => {
        if (mounted) setTrpcLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  // AC-107: Konten und Rollen laden. Beides zusammen, weil die Oberflaeche
  // beide fuer eine einzige Ansicht braucht; scheitert `principals.list` an
  // einem fehlenden Recht, ist das die Meldung, die zaehlt — `tenant.settings`
  // ist ANY_AUTHENTICATED und scheitert praktisch nie unabhaengig davon.
  useEffect(() => {
    if (!adminGeoeffnet) {
      return;
    }

    let mounted = true;
    setPrincipalsLoading(true);
    setPrincipalsError(null);
    setOptionenFehler(null);

    // AC-107-1: die Auswahl zum Verknüpfen nur mit `principals:manage` laden —
    // ohne das Recht schriebe der Aufruf eine Audit-Zeile (apps/web/CLAUDE.md).
    void Promise.all([
      trpc.principals.list.query(),
      trpc.tenant.settings.query(),
      // Eigener Fehlerweg (Review PR #85, B5): scheitert nur die Auswahl, bleibt
      // die Nutzerverwaltung bedienbar, und die Spalte zeigt nur an.
      darfVerknuepfen
        ? trpc.principals.employeeOptions.query().catch((fehler: unknown) => {
            if (mounted) {
              setOptionenFehler(
                `Die Personenauswahl konnte nicht geladen werden: ${
                  klassifiziereFehler(fehler, { schreibzugriff: false }).meldung
                }`,
              );
            }
            return null;
          })
        : Promise.resolve(null),
    ])
      .then(([geladenePrincipals, einstellungen, optionen]) => {
        if (mounted) {
          setPrincipals(geladenePrincipals);
          setRoles(einstellungen.roles);
          setMitarbeiterOptionen(optionen);
        }
      })
      .catch((fehler: unknown) => {
        if (mounted) {
          setPrincipalsError(klassifiziereFehler(fehler, { schreibzugriff: false }).meldung);
        }
      })
      .finally(() => {
        if (mounted) setPrincipalsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [adminGeoeffnet, darfVerknuepfen]);

  /**
   * Fuehrt eine Kontoaktion aus: serialisiert je Konto (CLAUDE.md §20.2,
   * Punkt 4 — zwei Aktionen auf demselben Konto sollen einander nicht
   * ueberholen), uebernimmt nach Erfolg die SERVERANTWORT (AC-016, AK-2) statt
   * optimistisch vorzugreifen, und traegt einen Fehlschlag in die Zeile des
   * betroffenen Kontos ein statt in eine globale Meldung — die anderen Zeilen
   * bleiben bedienbar.
   */
  async function fuehreKontoaktionAus(
    principalId: string,
    aufruf: () => Promise<Principal>,
  ): Promise<void> {
    setBusyIds((vorher) => new Set(vorher).add(principalId));

    try {
      const aktualisiert = await geteilterSerialisierer.reihe(
        entitaetsSchluessel('principal', principalId),
        aufruf,
      );

      setPrincipals((vorher) =>
        vorher.map((principal) => (principal.id === principalId ? aktualisiert : principal)),
      );
      setRowErrors((vorher) => {
        const neu = new Map(vorher);
        neu.delete(principalId);
        return neu;
      });
    } catch (fehler) {
      const befund = klassifiziereFehler(fehler, { schreibzugriff: true });
      setRowErrors((vorher) => new Map(vorher).set(principalId, meldungFuerKontoaktion(befund)));
    } finally {
      setBusyIds((vorher) => {
        const neu = new Set(vorher);
        neu.delete(principalId);
        return neu;
      });
    }
  }

  /**
   * Verknüpft ein Konto mit einer Person oder löst es (AC-107-1). Derselbe Weg
   * wie jede Kontoaktion — serialisiert je Konto, danach gilt die Serverantwort —,
   * nur mit eigener Meldung und nachgeführter Auswahl.
   */
  async function verknuepfe(principalId: string, employeeId: string | null): Promise<void> {
    const vorher = principals.find((principal) => principal.id === principalId);
    if (vorher === undefined) return;

    setBusyIds((alt) => new Set(alt).add(principalId));

    try {
      const nachher = await geteilterSerialisierer.reihe(
        entitaetsSchluessel('principal', principalId),
        () => trpc.principals.linkEmployee.mutate({ principalId, employeeId }),
      );

      setPrincipals((alt) =>
        alt.map((principal) => (principal.id === principalId ? nachher : principal)),
      );
      setMitarbeiterOptionen((alt) =>
        alt === null ? alt : optionenNachVerknuepfung(alt, vorher, nachher),
      );
      setRowErrors((alt) => {
        const neu = new Map(alt);
        neu.delete(principalId);
        return neu;
      });
    } catch (fehler) {
      const befund = klassifiziereFehler(fehler, { schreibzugriff: true });
      let optionen = mitarbeiterOptionen;

      // Ein Konflikt heißt: die eigene Auswahl ist veraltet. Neu laden, damit die
      // Person ausgegraut wird und die Meldung das andere Konto nennt (B5).
      if (befund.art === 'konflikt') {
        try {
          optionen = await trpc.principals.employeeOptions.query();
          setMitarbeiterOptionen(optionen);
          setOptionenFehler(null);
        } catch {
          // Die Meldung kommt dann ohne Kontonamen aus.
        }
      }

      const person = optionen?.find((option) => option.id === employeeId);
      setRowErrors((alt) =>
        new Map(alt).set(principalId, meldungFuerVerknuepfung(befund, person, principals)),
      );
    } finally {
      setBusyIds((alt) => {
        const neu = new Set(alt);
        neu.delete(principalId);
        return neu;
      });
    }
  }

  async function handleLogout(): Promise<void> {
    try {
      await logout();
      setSession(null);
      setRechte(null);
      setError(null);
    } catch {
      setError('Abmelden fehlgeschlagen. Bitte versuchen Sie es erneut.');
    }
  }

  return (
    <main>
      <h1>ASAS Core</h1>
      <SessionPanel
        session={session}
        loading={loading}
        error={error}
        onLogout={() => {
          void handleLogout();
        }}
      />
      <TrpcPanel echo={echo} loading={trpcLoading} error={trpcError} />
      {/* AC-601: zuerst, damit der Weg zur Meldung kurz ist (AK 4: höchstens drei Klicks). */}
      {session !== null && rechte !== null && ansichtSichtbar('restaufwand', rechte) && (
        <RestaufwandBereich
          key={`restaufwand:${session.subject}`}
          inhaber={session.subject}
          mandantenweit={rechte.has('remaining_effort:report_any')}
        />
      )}
      {rechte !== null && ansichtSichtbar('nutzerverwaltung', rechte) && (
        <AdminPanel
          geoeffnet={adminGeoeffnet}
          onOeffnen={() => {
            setAdminGeoeffnet(true);
          }}
          principals={principals}
          roles={roles}
          loading={principalsLoading}
          error={principalsError}
          busyIds={busyIds}
          rowErrors={rowErrors}
          onLock={(principalId) => {
            void fuehreKontoaktionAus(principalId, () =>
              trpc.principals.lock.mutate({ principalId }),
            );
          }}
          onUnlock={(principalId) => {
            void fuehreKontoaktionAus(principalId, () =>
              trpc.principals.unlock.mutate({ principalId }),
            );
          }}
          onAssignRole={(principalId, roleId) => {
            void fuehreKontoaktionAus(principalId, () =>
              trpc.principals.assignRole.mutate({ principalId, roleId }),
            );
          }}
          onRevokeRole={(principalId, roleId) => {
            void fuehreKontoaktionAus(principalId, () =>
              trpc.principals.revokeRole.mutate({ principalId, roleId }),
            );
          }}
          mitarbeiterOptionen={mitarbeiterOptionen}
          optionenFehler={optionenFehler}
          onLinkEmployee={(principalId, employeeId) => {
            void verknuepfe(principalId, employeeId);
          }}
        />
      )}
      {session !== null && rechte !== null && ansichtSichtbar('projekte', rechte) && (
        <Projekte
          key={session.subject}
          inhaber={session.subject}
          darfSchreiben={rechte.has('projects:manage')}
          darfStatus={darfStatusAendern(rechte)}
          darfArbeitspakete={ansichtSichtbar('arbeitspakete', rechte)}
          darfAnfragen={darfRessourcenAnfragen(rechte)}
        />
      )}
      {rechte !== null && ansichtSichtbar('arbeitspaketBibliothek', rechte) && (
        <ArbeitspaketBibliothek darfSchreiben={rechte.has('work_package_library:manage')} />
      )}
      {/* AC-302: lädt nur `tenant.settings`; gezeigt nur mit `work_package_library:manage` (E9). */}
      {session !== null &&
        rechte !== null &&
        ansichtSichtbar('apImport', rechte) &&
        darfApImport(rechte) && (
          <ApImportBereich
            key={`ap-import:${session.subject}`}
            inhaber={session.subject}
            darfFormatPflegen={darfApFormatPflegen(rechte)}
          />
        )}
      {/* AC-305: `tenant.settings` darf jeder lesen; gezeigt nur mit `tenant:configure` (E12). */}
      {session !== null &&
        rechte !== null &&
        ansichtSichtbar('lphGruppierung', rechte) &&
        darfLphGruppierungPflegen(rechte) && (
          <LphGruppierungBereich
            key={`lph-gruppierung:${session.subject}`}
            inhaber={session.subject}
            darfVorschau={darfLphVorschau(rechte)}
          />
        )}
      {/* AC-103/AC-104: nur mit passendem Recht sichtbar (ANSICHTEN); geladen wird erst auf Klick. */}
      {session !== null && rechte !== null && ansichtSichtbar('mitarbeiter', rechte) && (
        <MitarbeiterBereich inhaber={session.subject} />
      )}
      {session !== null && rechte !== null && ansichtSichtbar('fachgruppen', rechte) && (
        <FachgruppenBereich
          inhaber={session.subject}
          darfPflegen={darfFachgruppenPflegen(rechte)}
        />
      )}
      {/* AC-402: Auswahl mit `fachgruppe:read` (ANSICHTEN), das Raster nur mit `resource_allocation:read`. */}
      {rechte !== null &&
        ansichtSichtbar('wochenraster', rechte) &&
        darfWochenrasterLesen(rechte) && (
          <WochenrasterBereich
            key={`wochenraster:${session?.subject ?? ''}`}
            darfLesen={darfWochenrasterLesen(rechte)}
            darfOffeneAnfragen={darfOffeneAnfragenLesen(rechte)}
            darfEntscheiden={darfEntscheiden(rechte)}
            {...(session !== null && darfPlanen(rechte)
              ? { planen: { inhaber: session.subject, nachtragen: darfNachtragen(rechte) } }
              : {})}
          />
        )}
      {/* AC-407: nur mit `resource_allocation:read_aggregated` (ANSICHTEN). */}
      {rechte !== null && ansichtSichtbar('auslastung', rechte) && (
        <AuslastungBereich key={`auslastung:${session?.subject ?? ''}`} />
      )}
      <p>
        <small>
          Domain-Version <strong data-testid="domain-version">{DOMAIN_VERSION}</strong>
        </small>
      </p>
    </main>
  );
}
